import React, { useEffect } from "react";
import { Row, Col, Collapse, Icon, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import SwitchInput from "../../CommonFormItems/SwitchInput/SwitchInput";
import TextInput from "../../CommonFormItems/TextInput/TextInput";
import AsyncParticipantSelect from "../../CommonFormItems/AsyncParticipantSelect/AsyncParticipantSelect";

import ParticipantPostalCodeFieldsBlock from "../ParticipantPostalCodeFieldsBlock/ParticipantPostalCodeFieldsBlock";
import {
  participantsDetails,
  resetRecipientParticipantDetails,
  resetSenderParticipantDetails,
  updateParticipantsDetails,
} from "../../../../redux/slices/ParticipantsDetailsSlice";
import {
  resetParticipantDetails,
  buildParticipantFormAndReduxPayload,
} from "../../../../utils/participantUtils";

const { Panel } = Collapse;

const getParticipantList = async (participantName, action) => {
  const response = await action(participantName);
  return response;
};

const refineParticipantDetails = (participantType, participantDetails) => {
  if (!participantType) return {};
  const refinedParticipantDetails = {};
  for (let key in participantDetails) {
    const newKey = `${participantType}${key.charAt(0).toUpperCase()}${key.slice(
      1,
    )}`;
    refinedParticipantDetails[newKey] = participantDetails[key];
  }
  return refinedParticipantDetails;
};

export default function ParticipantBlock({
  form,
  participantType,
  getParticipants,
  requiredFields,
  serviceType,
  getPostalCodeRestriction,
  onPostalCodeValidated = () => {},
}) {
  const { getFieldDecorator, setFieldsValue } = form;
  const reduxParticipantDetails = useSelector(participantsDetails);
  const dispatch = useDispatch();
  const participantAssignedValue = participantType === "sender" ? 1 : 2;
  const participantTranslation =
    participantAssignedValue === 1 ? "remitente" : "destinatario";

  const proximityService = serviceType?.includes("Proximidad");

  useEffect(() => {
    if (
      participantType === "recipient" &&
      !reduxParticipantDetails?.[`${participantType}ParticipantId`] &&
      !reduxParticipantDetails?.[`${participantType}Name`]
    ) {
      const resetDetails = resetParticipantDetails(participantType, false);
      setFieldsValue(resetDetails);
    }
  }, [reduxParticipantDetails, participantType, setFieldsValue]);

  const selectedNameFromForm = form.getFieldValue(`${participantType}Name`);
  const selectedNameFromRedux =
    reduxParticipantDetails?.[`${participantType}Name`];
  const hasNameSelected = !!(selectedNameFromForm || selectedNameFromRedux);
  const isFormLocked = !hasNameSelected;

  const handleParticipantDetails = async (participantDetails, source) => {
    const resetDetails = resetParticipantDetails(
      participantType,
      false,
      participantDetails?.[`${participantType}Save`],
    );

    if (!participantDetails) {
      setFieldsValue(resetDetails);
      participantType === "sender"
        ? dispatch(resetSenderParticipantDetails())
        : dispatch(resetRecipientParticipantDetails());
      onPostalCodeValidated(participantType, null);
      return;
    }

    if (participantDetails?.[`${participantType}Name`]?.includes("Nuevo")) {
      if (participantType === "sender") {
        dispatch(resetSenderParticipantDetails());
      } else {
        dispatch(resetRecipientParticipantDetails());
      }
      dispatch(
        updateParticipantsDetails({
          [`${participantType}Name`]:
            participantDetails?.[`${participantType}Name`],
          [`${participantType}NewParticipant`]: true,
        }),
      );
      setFieldsValue(resetDetails);
      onPostalCodeValidated(participantType, null);
      return;
    }

    const refinedDetails = refineParticipantDetails(
      participantType,
      participantDetails,
    );

    const { nextReduxPayload, nextFormValues } =
      buildParticipantFormAndReduxPayload({
        participantType,
        refinedDetails,
        rawParticipant: participantDetails,
        source,
      });

    dispatch(updateParticipantsDetails(nextReduxPayload));
    setFieldsValue(nextFormValues);

    if (proximityService) {
      const postCode = refinedDetails[`${participantType}PostalCode`];

      if (participantType === "sender") {
        const restrictionResult = await getPostalCodeRestriction(postCode);
        if (!restrictionResult || restrictionResult.length === 0) {
          notification.error({
            message: "Acción no permitida",
            description: `El código postal de remitente ${postCode} no permite realizar el servicio Frio Proximidad.`,
          });
          setFieldsValue(resetDetails);
          dispatch(resetSenderParticipantDetails());
          onPostalCodeValidated(participantType, null);
          return;
        }
        onPostalCodeValidated(participantType, postCode);
      }

      if (participantType === "recipient") {
        const senderPostCode = reduxParticipantDetails.senderPostalCode;
        const checkPostalCode = await getPostalCodeRestriction(senderPostCode);

        const match = checkPostalCode?.find(
          (item) =>
            item.originPostalCode === senderPostCode &&
            item.destinationPostalCode === postCode,
        );

        if (!match) {
          notification.error({
            message: "Acción no permitida",
            description: `No está permitido realizar el servicio Frio Proximidad para el código postal ${postCode} desde el código postal de remitente.`,
          });

          dispatch(resetRecipientParticipantDetails());
          onPostalCodeValidated(participantType, null);
          return;
        }
        onPostalCodeValidated(participantType, postCode);
        dispatch(
          updateParticipantsDetails({
            [`${participantType}PostalCode`]: postCode,
          }),
        );
      }
    }
  };

  return (
    <Row className="form-section">
      <Collapse
        defaultActiveKey={["1"]}
        expandIcon={({ isActive }) => (
          <Icon type="caret-right" rotate={isActive ? 90 : 0} />
        )}
      >
        <Panel
          header={
            participantTranslation?.charAt(0).toUpperCase() +
            participantTranslation?.slice(1)
          }
          key="1"
          extra={<Icon type="minus-circle" />}
          className="Busca"
        >
          <div className="switch-container">
            {!reduxParticipantDetails?.[`${participantType}ParticipantId`] &&
            reduxParticipantDetails?.[`${participantType}Name`] ? (
              <SwitchInput
                getFieldDecorator={getFieldDecorator}
                name={`${participantType}Save`}
                label={`Guardar datos de ${participantTranslation} como nuevo contacto`}
                size="small"
                setFieldsValue={setFieldsValue}
                disabled={isFormLocked}
              />
            ) : null}
          </div>
          {participantType === "recipient" && (
            <Row type="flex" justify="space-between" gutter={[16, 16]}  className="pr-grid-row">
              <Col xs={24} md={11} className="pr-col">
                <AsyncParticipantSelect
                  label="Buscador de destinatario particular/empresa por Alias"
                  getFieldDecorator={getFieldDecorator}
                  name={`${participantType}Alias`}
                  required={false}
                  proximityService={proximityService}
                  getParticipantList={(e) =>
                    getParticipantList(e, getParticipants)
                  }
                  handleParticipantDetails={(participantDetails) =>
                    handleParticipantDetails(participantDetails, "alias")
                  }
                  setFieldsValue={setFieldsValue}
                  contactType="alias"
                  getFieldsValue={form.getFieldsValue}
                  labelField="alias"
                  searchField="alias"
                  allowCreate={false}
                  noResultsText="No existe ningún alias coincidente con los criterios indicados"
                />
              </Col>
              <Col
                xs={24}
                md={7}
                className="hidden-column pr-col pr-col-offset"
              ></Col>
            </Row>
          )}
          <Row type="flex" justify="space-between" gutter={[16, 16]}  className="pr-grid-row">
            <Col xs={24} md={7} className="hidden-column pr-col pr-col-offset">
              <TextInput
                getFieldDecorator={getFieldDecorator}
                name={`${participantType}ParticipantId`}
                initialValue={
                  reduxParticipantDetails?.[`${participantType}ParticipantId`]
                }
                disabled
              />
            </Col>

            <Col xs={24} md={11} className="pr-col">
              <AsyncParticipantSelect
                label={`Nombre de ${participantTranslation} particular/empresa`}
                getFieldDecorator={getFieldDecorator}
                name={`${participantType}Name`}
                required
                requiredMessage="Campo obligatorio"
                getParticipantList={(e) =>
                  getParticipantList(e, getParticipants)
                }
                handleParticipantDetails={(participantDetails) =>
                  handleParticipantDetails(participantDetails, "name")
                }
                setFieldsValue={setFieldsValue}
                contactType={participantTranslation}
                getFieldsValue={form.getFieldsValue}
                proximityService={proximityService}
              />
            </Col>

            <Col xs={24} md={11} className="pr-col pr-col-offset">
              <TextInput
                label="Dirección"
                getFieldDecorator={getFieldDecorator}
                name={`${participantType}Address`}
                initialValue={
                  reduxParticipantDetails?.[`${participantType}Address`]
                }
                disabled={
                  isFormLocked ||
                  !!reduxParticipantDetails?.[`${participantType}Address`]
                }
                required
                requiredMessage="Campo obligatorio"
                placeholder="Ej: Calle de los Ríos, 24, 3ºB"
                pattern={/^[^\s].{3,198}[^\s]$/u}
                patternMessage="Valor no válido. Están permitidos entre 5 y 200 caracteres, sin espacios al inicio o fin del texto."
              />
            </Col>
          </Row>

          <ParticipantPostalCodeFieldsBlock
            participantType={participantType}
            initialFormValues={reduxParticipantDetails}
            form={form}
            isPreregister={true}
            isNewParticipant={
              reduxParticipantDetails?.[`${participantType}NewParticipant`]
            }
            getFieldsValue={form.getFieldsValue}
            proximityService={proximityService}
            getPostalCodeRestriction={getPostalCodeRestriction}
            onPostalCodeValidated={onPostalCodeValidated}
            disabled={isFormLocked}
          />

          <Row type="flex" justify="space-between" gutter={[16, 16]}  className="pr-grid-row">
            <Col xs={24} md={7} className="pr-col">
              <TextInput
                label="Email"
                getFieldDecorator={getFieldDecorator}
                name={`${participantType}Email`}
                initialValue={
                  reduxParticipantDetails?.[`${participantType}Email`]
                }
                disabled={
                  isFormLocked ||
                  !!reduxParticipantDetails?.[`${participantType}Email`]
                }
                required
                requiredMessage="Campo obligatorio"
                pattern={/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/}
                patternMessage={"E-mail no válido."}
                placeholder="Ej: luissanchez@examplemail.com"
              />
            </Col>
            <Col xs={24} md={7} className="pr-col pr-col-offset">
              <TextInput
                label="Teléfono móvil"
                getFieldDecorator={getFieldDecorator}
                name={`${participantType}MobilePhone`}
                initialValue={
                  reduxParticipantDetails?.[`${participantType}MobilePhone`]
                }
                disabled={
                  isFormLocked ||
                  !!reduxParticipantDetails?.[`${participantType}MobilePhone`]
                }
                required
                placeholder="Ej: 616237810"
                requiredMessage="Campo obligatorio"
                pattern={/^[67]\d{8}$/}
                maxLength={9}
              />
            </Col>
            <Col xs={24} md={7} className="pr-col pr-col-offset">
              <TextInput
                label="NIF/NIE/Otros"
                getFieldDecorator={getFieldDecorator}
                name={`${participantType}DocumentId`}
                initialValue={
                  reduxParticipantDetails?.[`${participantType}DocumentId`]
                }
                disabled={
                  isFormLocked ||
                  !!reduxParticipantDetails?.[`${participantType}DocumentId`]
                }
                placeholder="Introduzca NIF/NIE/Otros"
                pattern={/^(?! )[A-Za-z0-9 ]{5,15}(?<! )$/}
                patternMessage="Valor no válido. Están permitidos entre 5 y 15 caracteres, sin espacios al inicio o fin del texto."
              />
            </Col>
          </Row>

          <Row type="flex" justify="space-between" gutter={[16, 16]}  className="pr-grid-row">
            <Col xs={24} md={7} className="pr-col">
              <TextInput
                label="Persona de contacto"
                getFieldDecorator={getFieldDecorator}
                name={`${participantType}ContactPerson`}
                initialValue={
                  reduxParticipantDetails?.[`${participantType}ContactPerson`]
                }
                disabled={
                  isFormLocked ||
                  !!reduxParticipantDetails?.[`${participantType}ContactPerson`]
                }
                placeholder="Ej: Luis Martínez Sigüenza"
                pattern={/^[^\s][\p{L}0-9.,\s]{1,298}[^\s]$/u}
                patternMessage="Valor no válido. Están permitidos entre 3 y 300 caracteres sin espacios al inicio o fin del texto."
              />
            </Col>
            <Col xs={24} md={7} className="pr-col pr-col-offset">
              <TextInput
                label="Teléfono de contacto"
                getFieldDecorator={getFieldDecorator}
                name={`${participantType}ContactPhone`}
                initialValue={
                  reduxParticipantDetails?.[`${participantType}ContactPhone`]
                }
                disabled={
                  isFormLocked ||
                  !!reduxParticipantDetails?.[`${participantType}ContactPhone`]
                }
                placeholder="Ej: 661234572 ó 912345678"
                required
                requiredMessage="Campo obligatorio"
                pattern={/^\d{9,15}$/}
                maxLength={15}
              />
            </Col>
            <Col xs={24} md={7} className="pr-col pr-col-offset">
              {participantType === "sender" ? (
                <TextInput
                  label="Nº de cuenta bancaria"
                  getFieldDecorator={getFieldDecorator}
                  name={`${participantType}BankAccount`}
                  initialValue={
                    reduxParticipantDetails?.[`${participantType}BankAccount`]
                  }
                  disabled={
                    isFormLocked ||
                    !!reduxParticipantDetails?.[`${participantType}BankAccount`]
                  }
                  required={requiredFields?.paymentOnDelivery}
                  requiredMessage="Campo obligatorio"
                  placeholder="Ej: ES6214674408552741078583 (sin espacios)"
                  pattern={/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,34}$/}
                  patternMessage="Iban no válido. El número no puede contener espacios."
                />
              ) : null}
            </Col>
          </Row>
        </Panel>
      </Collapse>
    </Row>
  );
}
